import React from 'react';
import { useInView } from 'react-intersection-observer';
import { format, parseISO, isBefore, isAfter } from 'date-fns';
import { FaMapMarkerAlt, FaClock } from 'react-icons/fa';
import { FaCirclePlay } from 'react-icons/fa6';
import * as StyledComponents from './StyledComponents';
import { EventData } from '../types';
import puntoticketIcon from '../assets/icons/puntoticket.ico';
import comediaticketIcon from '../assets/icons/comediaticket.ico';
import ticketmasterIcon from '../assets/icons/ticketmaster.png';
import passlineIcon from '../assets/icons/passline.ico';
import ticketplusIcon from '../assets/icons/ticketplus.png';
import { es } from 'date-fns/locale';

const categoryColors: { [key: string]: string } = {
  'Música': '#1E90FF',      // Dodger Blue
  'Arte y Teatro': '#FF69B4', // Hot Pink
  'Feria y Expo': '#32CD32', // Lime Green
  'Deporte': '#FFA500',     // Orange
  'Comedia': '#DF3636',     // Red
  'Discoteca': '#00008B',      // Dark Blue
  'Experiencia': '#b42eff', // Teal
  'Cine': '#800000', // Brown
  'Otros': '#767676'        // Gray
};

const PROVIDER_ICONS: { [key: string]: string } = {
  'PuntoTicket': puntoticketIcon,
  'ComediaTicket': comediaticketIcon,
  'Ticketmaster': ticketmasterIcon,
  'Passline': passlineIcon,
  'TicketPlus': ticketplusIcon,
  // Add other providers as needed
};

interface EventCardProps {
  event: EventData;
  isRowVisible: boolean;
  eventGridId: string;
}

const EventCard: React.FC<EventCardProps> = ({ event, isRowVisible, eventGridId }) => {
  const { ref: horizontalRef, inView: horizontalInView } = useInView({
    triggerOnce: true,
    rootMargin: '0px 300px',
    root: document.getElementById(eventGridId),
  });

  const [imageLoaded, setImageLoaded] = React.useState(false);
  const [imageError, setImageError] = React.useState(false);

  const handleImageLoad = () => setImageLoaded(true);
  const handleImageError = () => setImageError(true);

  const handleClick = () => {
    const urlWithUtm = `${event.url}`; // TODO: define utms tags const urlWithUtm = `${event.url}${event.url.includes('?') ? '&' : '?'}${utmParams.toString()}`;
    window.open(urlWithUtm, '_blank', 'noopener,noreferrer');
  };

  const handleLocationClick = (e: React.MouseEvent) => {
    e.stopPropagation(); // Prevent triggering the parent card's click
    const encodedLocation = encodeURIComponent(event.location);
    const mapsUrl = `https://www.google.com/maps/search/?api=1&query=${encodedLocation}`;
    window.open(mapsUrl, '_blank', 'noopener,noreferrer');
  };

  // Get the color for the category, default to 'Otros' if not found
  const categoryColor = categoryColors[event.category] || categoryColors['Otros'];

  // Calculate the minimum price across all datetimes
  const minPrice = event.datetimes.reduce((min, dt) => {
    if (dt.min_price === null) return min;
    return min === null || dt.min_price < min ? dt.min_price : min;
  }, null as number | null);

  const providerIcon = PROVIDER_ICONS[event.source];

  return (
    <StyledComponents.EventCard 
      ref={horizontalRef}
      onClick={handleClick}
    >
      <StyledComponents.EventImageContainer>
        {horizontalInView && isRowVisible && (
          <>
            {!imageError && (
              <StyledComponents.EventImage 
                src={event.image_url} 
                alt={event.title}
                onLoad={handleImageLoad}
                onError={handleImageError}
                style={{ opacity: imageLoaded ? 1 : 0 }}
              />
            )}
            {(!imageLoaded || imageError) && (
              <StyledComponents.ImagePlaceholder>
                {imageError ? 'Imagen no disponible' : ''}
              </StyledComponents.ImagePlaceholder>
            )}
            <StyledComponents.CategoryPill color={categoryColor}>
              {event.category}
            </StyledComponents.CategoryPill>  
            <StyledComponents.EventImageOverlay>
              <StyledComponents.ProviderIcon 
                src={providerIcon} 
              />
              {minPrice ? `Entradas desde $${minPrice.toLocaleString('es-CL')}` : 'Ver entradas'}
            </StyledComponents.EventImageOverlay>
          </>
        )}
      </StyledComponents.EventImageContainer>
      <StyledComponents.EventContent>
        <h2>{event.title}</h2>
        <StyledComponents.EventDetailContainer>
          <FaMapMarkerAlt />
          <StyledComponents.ClickableSpan onClick={handleLocationClick}>
            {event.location}
          </StyledComponents.ClickableSpan>
        </StyledComponents.EventDetailContainer>
        <StyledComponents.EventDetailContainer>
          {event.datetimes.some(dt => {
            const start = parseISO(dt.start);
            const end = dt.end ? parseISO(dt.end) : null;
            const now = new Date();
            return isBefore(start, now) && end && isAfter(end, now);
          }) ? (
            <FaCirclePlay style={{ color: '#ff0000', opacity: 0.8 }} />
          ) : (
            <FaClock />
          )}
          <span>
            {event.datetimes
              .slice(0, 3)
              .map(dt => {
                const start = parseISO(dt.start);
                const end = dt.end ? parseISO(dt.end) : null;
                const now = new Date();
                
                if (isBefore(start, now) && end && isAfter(end, now)) {
                  // Check if end is more than 24 hours from start
                  const hoursDiff = (end.getTime() - start.getTime()) / (1000 * 60 * 60);
                  if (hoursDiff > 24) {
                    return `En curso - hasta las ${format(end, 'HH:mm')} del ${format(end, 'EEE d MMM', { locale: es })}`;
                  }
                  return `En curso - hasta las ${format(end, 'HH:mm')}`;
                }
                return format(start, 'HH:mm');
              })
              .join(', ')}
            {event.datetimes.length > 3 && 
              ` y ${event.datetimes.length - 3} horarios más`}
          </span>
        </StyledComponents.EventDetailContainer>
      </StyledComponents.EventContent>
    </StyledComponents.EventCard>
  );
};

export default EventCard;
